import { lastSeen, profileInput, USER_ACTIVITY_FILTERS } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BanIcon,
  CheckCircle2Icon,
  ClockIcon,
  MailIcon,
  MoreHorizontalIcon,
  SearchIcon,
  ShieldIcon,
  UsersIcon,
  UserXIcon,
} from "lucide-react";
import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Avatar } from "@/components/app/avatar";
import { EmptyState } from "@/components/app/empty-state";
import { Field } from "@/components/app/field";
import { Page, PageHeader } from "@/components/app/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { displayName, useMe } from "@/lib/account";
import { errorMessage } from "@/lib/errors";
import { useFormat } from "@/lib/format";
import { navigate, PageLink, useLocation } from "@/lib/pages";
import { type AdminOutputs, useAdminTRPC } from "@/lib/trpc";
import { FormError } from "../account/sign-in";

type AdminUser = AdminOutputs["users"]["list"][number];
type AdminInvitation = AdminOutputs["users"]["invitations"][number];

/**
 * Everyone with an account: search, admin role, disable, delete. The filters find who never came
 * back, who went quiet and who was invited without an account yet; `?filter=` keeps them, so the
 * admin app can link to them (ADR 0016).
 */
export function UsersPage() {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const { search } = useLocation();
  const filter = USER_ACTIVITY_FILTERS.find((f) => f === search.get("filter")) ?? null;
  const [query, setQuery] = useState("");
  const deferred = useDeferredValue(query);
  const { data: users } = useQuery({
    ...trpc.users.list.queryOptions({
      query: deferred,
      ...(filter && filter !== "invited" && { filter }),
    }),
    enabled: filter !== "invited",
  });
  const { data: invitations } = useQuery({
    ...trpc.users.invitations.queryOptions({ query: deferred }),
    enabled: filter === "invited",
  });
  const { data: me } = useMe();
  const choose = (value: string) =>
    navigate(value === "all" ? "/admin/users" : `/admin/users?filter=${value}`, { replace: true });

  return (
    <Page>
      <PageHeader title={t("admin.users")} subtitle={t("admin.usersBody")} />
      <Tabs value={filter ?? "all"} onValueChange={choose} className="mb-4">
        <TabsList aria-label={t("admin.filter")}>
          <TabsTrigger value="all">{t("admin.filterAll")}</TabsTrigger>
          {USER_ACTIVITY_FILTERS.map((f) => (
            <TabsTrigger key={f} value={f}>
              {t(`admin.filters.${f}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {filter ? (
        <p className="mb-4 text-sm text-muted-foreground">{t(`admin.filterHints.${filter}`)}</p>
      ) : null}
      <div className="relative mb-4 max-w-sm">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label={t("admin.search")}
          placeholder={t("admin.search")}
          className="pl-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {filter === "invited" ? (
        <Invitations invitations={invitations} />
      ) : (
        <>
          {users && !users.length ? (
            <EmptyState icon={UsersIcon} title={t("admin.noUsers")} />
          ) : null}
          {users?.length ? (
            <Card className="divide-y divide-border">
              {users.map((user) => (
                <UserRow key={user.id} user={user} isMe={user.id === me?.id} />
              ))}
            </Card>
          ) : null}
        </>
      )}
    </Page>
  );
}

/** The people invited who have no account yet; their garage's page sends an invitation again. */
function Invitations({ invitations }: { invitations: AdminInvitation[] | undefined }) {
  const { t } = useTranslation();
  const format = useFormat();
  if (invitations && !invitations.length) {
    return <EmptyState icon={MailIcon} title={t("admin.noInvitations")} />;
  }
  if (!invitations?.length) return null;
  return (
    <Card className="divide-y divide-border">
      {invitations.map((invitation) => (
        <div key={invitation.id} className="flex items-start gap-3 px-4 py-3">
          <MailIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {invitation.firstName} {invitation.lastName}
            </p>
            <p className="truncate text-xs text-muted-foreground">{invitation.email}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {invitation.state === "expired" ? (
                <Badge tone="missing">
                  <ClockIcon />
                  {t("memberStatus.expired")}
                </Badge>
              ) : (
                <Badge tone="partial">
                  <ClockIcon />
                  {t("memberStatus.invited", { date: format.date(invitation.expiresAt) })}
                </Badge>
              )}
              {invitation.garage ? (
                <PageLink href={`/admin/garages/${invitation.garage}`}>
                  <Badge tone="outline" className="hover:bg-muted">
                    {invitation.garage}
                    {invitation.role ? ` · ${t(`roles.${invitation.role}`)}` : ""}
                  </Badge>
                </PageLink>
              ) : (
                <Badge tone="brand">
                  <ShieldIcon />
                  {t("roles.admin")}
                </Badge>
              )}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t("admin.invitedOn", { date: format.date(invitation.createdAt) })}
            </p>
          </div>
        </div>
      ))}
    </Card>
  );
}

/** "Last seen", or the last sign-in before tracking began, labelled as such. */
function LastSeen({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const format = useFormat();
  const seen = lastSeen(user);
  if (!seen) return <>{`${t("admin.lastSeen")}: ${t("admin.never")}`}</>;
  return (
    <>
      {`${t("admin.lastSeen")}: ${format.date(seen.on)}`}
      {seen.beforeTracking ? ` (${t("admin.beforeTracking")})` : ""}
    </>
  );
}

function StatusBadge({ status }: { status: AdminUser["status"] }) {
  const { t } = useTranslation();
  if (status === "active") {
    return (
      <Badge tone="ok">
        <CheckCircle2Icon />
        {t("userStatus.active")}
      </Badge>
    );
  }
  return status === "disabled" ? (
    <Badge tone="missing">
      <BanIcon />
      {t("userStatus.disabled")}
    </Badge>
  ) : (
    <Badge tone="neutral">
      <UserXIcon />
      {t("userStatus.deleted")}
    </Badge>
  );
}

function UserRow({ user, isMe }: { user: AdminUser; isMe: boolean }) {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const format = useFormat();
  const setAdmin = useMutation(trpc.users.setAdmin.mutationOptions());
  const setDisabled = useMutation(trpc.users.setDisabled.mutationOptions());
  const remove = useMutation(trpc.users.delete.mutationOptions());
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const name = user.status === "deleted" ? t("userStatus.deleted") : displayName(user);

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Avatar user={user} className="mt-0.5 size-9" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {name}
          {isMe ? (
            <span className="font-normal text-muted-foreground"> ({t("admin.you")})</span>
          ) : null}
        </p>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {user.platformRole === "admin" ? (
            <Badge tone="brand">
              <ShieldIcon />
              {t("roles.admin")}
            </Badge>
          ) : null}
          <StatusBadge status={user.status} />
          {user.garages.length ? (
            user.garages.map((g) => (
              <PageLink key={g.name} href={`/admin/garages/${g.name}`}>
                <Badge tone="outline" className="hover:bg-muted">
                  {g.name} · {t(`roles.${g.role}`)}
                  {g.status === "deactivated" ? ` · ${t("memberStatus.deactivated")}` : ""}
                </Badge>
              </PageLink>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">{t("admin.noGarage")}</span>
          )}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          <LastSeen user={user} /> · {t("admin.created")}: {format.date(user.createdAt)}
        </p>
      </div>
      {isMe || user.status === "deleted" ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t("admin.userActions", { name })}>
              <MoreHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setRenaming(true)}>
              {t("admin.renameUser")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                setAdmin.mutate({ userId: user.id, admin: user.platformRole !== "admin" })
              }
            >
              {user.platformRole === "admin" ? t("admin.removeAdmin") : t("admin.makeAdmin")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                setDisabled.mutate({ userId: user.id, disabled: user.status === "active" })
              }
            >
              {user.status === "active" ? t("admin.disable") : t("admin.enable")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => setDeleting(true)}
            >
              {t("admin.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t("admin.deleteTitle", { name })}
        description={t("admin.deleteBody")}
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate({ userId: user.id }, { onSettled: () => setDeleting(false) })
        }
      />
      <RenameUserDialog user={user} name={name} open={renaming} onOpenChange={setRenaming} />
    </div>
  );
}

/** Corrects a user's name (ADR 0011); they change it themselves in their profile too. */
function RenameUserDialog({
  user,
  name,
  open,
  onOpenChange,
}: {
  user: AdminUser;
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const rename = useMutation({ ...trpc.users.rename.mutationOptions(), meta: { silent: true } });
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader title={t("admin.renameUserTitle", { name })} />
        <form
          className="contents"
          onSubmit={async (event) => {
            event.preventDefault();
            const parsed = profileInput.safeParse({ firstName, lastName });
            if (!parsed.success) {
              setErrors(
                Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
              );
              return;
            }
            setErrors({});
            try {
              await rename.mutateAsync({ userId: user.id, ...parsed.data });
              toast.success(t("admin.userRenamed"));
              onOpenChange(false);
            } catch (error) {
              setServerError(errorMessage(error, t));
            }
          }}
        >
          <DialogBody className="grid gap-4 sm:grid-cols-2">
            <Field label={t("account.firstName")} htmlFor="rename-first" error={errors.firstName}>
              <Input
                id="rename-first"
                autoFocus
                autoComplete="off"
                value={firstName}
                aria-invalid={errors.firstName ? true : undefined}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field label={t("account.lastName")} htmlFor="rename-last" error={errors.lastName}>
              <Input
                id="rename-last"
                autoComplete="off"
                value={lastName}
                aria-invalid={errors.lastName ? true : undefined}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
            <div className="sm:col-span-2">
              <FormError message={serverError} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={rename.isPending}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
